import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import {
  AnyTransactionsData,
  GroupTransactionsData,
  OrgTransactionsData,
  PricebookTransactions,
  RevShareSummary,
  SubscriptionCharge,
  TeamTransactionsData,
} from '../../../models/RevShareData';
import { AdminTierTypeEnum } from '../../../services/drillDown';
import type { RevShareRegistry } from '..';
import { toDateKey } from '../../../helpers/utilityFunctions';
import { sortAlphabetically, sortByDate } from '../../../helpers/sortUtils';

dayjs.extend(quarterOfYear);

export const getRevShareCompartmentKey = (
  tier: AdminTierTypeEnum | undefined,
): keyof RevShareRegistry | null => {
  switch (tier) {
    case AdminTierTypeEnum.Root:
      return 'orgRevShareTransactions';
    case AdminTierTypeEnum.Organization:
      return 'teamRevShareTransactions';
    case AdminTierTypeEnum.Team:
      return 'groupRevShareTransactions';
    case AdminTierTypeEnum.Group:
    default:
      return null;
  }
};

export const createRevShareKey = (startDate: string, endDate: string): string => {
  return `${startDate}::${endDate}`;
};

export const parseRevShareKey = (key: string): { startDate: string; endDate: string } => {
  const [startDate, endDate] = key.split('::');
  return { startDate, endDate };
};

export enum DateRangeType {
  LastMonth = 'Last Month',
  LastQuarter = 'Last Quarter',
  LastYear = 'Last Year',
  Custom = 'Custom Date Range',
}

export interface TransactionsDateRange {
  rangeType: DateRangeType;
  startDate?: string;
  endDate?: string;
}

export const getLastYearRange = (): TransactionsDateRange => {
  const lastYear = dayjs().subtract(1, 'year');
  return {
    rangeType: DateRangeType.LastYear,
    startDate: toDateKey(lastYear.startOf('year')),
    endDate: toDateKey(lastYear.endOf('year')),
  };
};

export const getLastQuarterRange = (): TransactionsDateRange => {
  const lastQuarter = dayjs().subtract(1, 'quarter');
  return {
    rangeType: DateRangeType.LastQuarter,
    startDate: toDateKey(lastQuarter.startOf('quarter')),
    endDate: toDateKey(lastQuarter.endOf('quarter')),
  };
};

export const getLastMonthRange = (): TransactionsDateRange => {
  const today = dayjs();
  return {
    rangeType: DateRangeType.LastMonth,
    startDate: toDateKey(today.subtract(1, 'month').startOf('month')),
    endDate: toDateKey(today.subtract(1, 'month').endOf('month')),
  };
};

export const getCustomRange = (): TransactionsDateRange => {
  return {
    rangeType: DateRangeType.Custom,
    startDate: undefined,
    endDate: undefined,
  };
};

export const isValidCustomRange = (dateRange: TransactionsDateRange): boolean => {
  if (dateRange.rangeType !== DateRangeType.Custom) return true;
  const { startDate, endDate } = dateRange;
  if (startDate == null || endDate == null) return false;
  return dayjs(startDate).isBefore(dayjs(endDate), 'day');
};

export const formatDateForFilename = (date: string | undefined): string => {
  if (!date) return 'unknown-date';
  const [year, month, day] = date.split('-');
  return `${month}-${day}-${year.slice(2)}`;
};

export const sanitizeForFilename = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const buildPdfFilename = (
  entityName: string,
  startDate: string | undefined,
  endDate: string | undefined,
): string => {
  const name = sanitizeForFilename(entityName) || 'rev-share-report';
  const start = formatDateForFilename(startDate);
  const end = formatDateForFilename(endDate);
  return `transaction-history-${name}-${start}-${end}.pdf`;
};

export const getUniqueKey = (
  pricebook: PricebookTransactions,
  revShareSummary?: RevShareSummary,
): string => {
  const paidBy = pricebook.chargeInfos[0]?.paidBy || 'UNKNOWN';
  const revShareMethod = revShareSummary?.revShareDorp || 'NONE';
  const revShareValue =
    revShareSummary?.revShareDorp === 'P'
      ? revShareSummary.revSharePct
      : revShareSummary?.fixedAmount;

  return `${pricebook.pricebookId}-${paidBy}-${revShareMethod}-${revShareValue}`;
};

export const getRevShareMethod = (revShareSummary?: RevShareSummary): string => {
  if (!revShareSummary?.revShareDorp) return 'none';

  if (revShareSummary.revShareDorp === 'D') {
    return `$${revShareSummary.fixedAmount.toFixed(2)}`;
  }

  if (revShareSummary.revShareDorp === 'P') {
    return `${revShareSummary.revSharePct}%`;
  }

  return 'none';
};

export const sortCharges = (charges: SubscriptionCharge[]) =>
  [...(charges ?? [])].sort(
    (a, b) => new Date(b.datePaid).getTime() - new Date(a.datePaid).getTime(),
  );

export const splitPricebooksBySummary = (
  pricebooks: PricebookTransactions[],
): PricebookTransactions[] => {
  return pricebooks.flatMap(pricebook => {
    if (pricebook.revShareSummaries.length <= 1) {
      return [pricebook];
    }

    const sortedSummaries = [...pricebook.revShareSummaries].sort((a, b) => {
      const aTime = a.effectiveTimestamp ? new Date(a.effectiveTimestamp).getTime() : 0;
      const bTime = b.effectiveTimestamp ? new Date(b.effectiveTimestamp).getTime() : 0;
      return aTime - bTime;
    });

    return sortedSummaries
      .map(summary => {
        const { effectiveTimestamp, expireTimestamp } = summary;
        const periodStart = effectiveTimestamp ? new Date(effectiveTimestamp).getTime() : null;
        const periodEnd = expireTimestamp ? new Date(expireTimestamp).getTime() : null;

        const charges = pricebook.chargeInfos.filter(charge => {
          const paidTime = new Date(charge.datePaid).getTime();

          if (periodStart !== null && paidTime < periodStart) {
            return false;
          }

          if (periodEnd !== null && paidTime >= periodEnd) {
            return false;
          }

          return true;
        });

        return {
          ...pricebook,
          chargeInfos: charges,
          quantity: charges.reduce((sum, c) => sum + c.quantity, 0),
          revShareSummaries: [summary],
        };
      })
      .filter(slice => slice.chargeInfos.length > 0);
  });
};

export const sortPricebooks = (pricebooks: PricebookTransactions[]) =>
  splitPricebooksBySummary([...pricebooks])
    .map(pricebook => ({
      ...pricebook,
      chargeInfos: sortCharges(pricebook.chargeInfos),
    }))
    .sort((a, b) => {
      const nameCompare = sortAlphabetically(a.pricebookName, b.pricebookName);
      if (nameCompare !== 0) return nameCompare;
      return sortByDate(
        a.revShareSummaries[0]?.effectiveTimestamp,
        b.revShareSummaries[0]?.effectiveTimestamp,
      );
    });

export const sortOrgs = (orgs: OrgTransactionsData[]) =>
  [...orgs]
    .sort((a, b) => sortAlphabetically(a.orgName, b.orgName))
    .map(org => ({
      ...org,
      pricebookInfos: org.pricebookInfos?.length
        ? sortPricebooks(org.pricebookInfos)
        : org.pricebookInfos,
    }));

export const sortTeams = (teams: TeamTransactionsData[]) =>
  [...teams]
    .sort((a, b) => sortAlphabetically(a.teamName, b.teamName))
    .map(team => ({
      ...team,
      orgInfos: team.orgInfos?.length ? sortOrgs(team.orgInfos) : team.orgInfos,
    }));

export const sortTransactionsData = (
  data: AnyTransactionsData | undefined,
  tier: AdminTierTypeEnum | undefined,
): AnyTransactionsData | undefined => {
  if (!data || !tier) return data;

  switch (tier) {
    case AdminTierTypeEnum.Root: {
      const orgData = data as OrgTransactionsData;
      return orgData.pricebookInfos?.length
        ? { ...orgData, pricebookInfos: sortPricebooks(orgData.pricebookInfos) }
        : orgData;
    }

    case AdminTierTypeEnum.Organization: {
      const teamData = data as TeamTransactionsData;
      return teamData.orgInfos?.length
        ? { ...teamData, orgInfos: sortOrgs(teamData.orgInfos) }
        : teamData;
    }

    case AdminTierTypeEnum.Team: {
      const groupData = data as GroupTransactionsData;
      return groupData.teamInfos?.length
        ? { ...groupData, teamInfos: sortTeams(groupData.teamInfos) }
        : groupData;
    }

    default:
      return data;
  }
};
